import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getMyContentInfiniteOptions } from '@/utils/queryOptions';

// The current creator's own content from /content/me — the only source that includes
// unpublished work. Includes anything MyOmnia has removed, deliberately: a creator should
// see that something of theirs was taken down, and the card badges it as Removed.
//
// Paginated as an infinite query. `totalItems` is the server's count of the whole shelf,
// which is what a "how much have I made" stat wants — `content.length` only counts the
// pages loaded so far.
//
// `enabled` is not optional politeness: /content/me is role-gated to creators and super
// admins, and the creator profile is reachable by an applicant whose role has not been
// granted yet. Calling it for them returns 403 and paints an error state over what should
// be a perfectly good pending profile.
const useMyContent = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({ ...getMyContentInfiniteOptions(), enabled });

  const content = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data?.pages]);
  const totalItems = data?.pages[0]?.pagination?.totalItems ?? content.length;

  return { content, totalItems, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage };
};

export default useMyContent;
